/** Тонкая обёртка над window.Telegram.WebApp.
 *  Не тянем SDK-пакет: нужен ровно этот десяток вызовов, а официальный
 *  скрипт уже подключён в index.html. */

type ThemeParams = Record<string, string>

interface TelegramWebApp {
  initData: string
  initDataUnsafe: {
    user?: { id: number; first_name?: string; language_code?: string; allows_write_to_pm?: boolean }
    start_param?: string
  }
  openLink: (url: string) => void
  openTelegramLink: (url: string) => void
  requestWriteAccess?: (cb?: (granted: boolean) => void) => void
  /** Bot API 8.0+: отправка подготовленного ботом сообщения в выбранный чат */
  shareMessage?: (msgId: string, cb?: (sent: boolean) => void) => void
  isVersionAtLeast?: (version: string) => boolean
  /** Bot API 8.0+: скачать файл по ссылке (Telegram спросит, куда сохранить) */
  downloadFile?: (params: { url: string; file_name: string }, cb?: (accepted: boolean) => void) => void
  colorScheme: 'light' | 'dark'
  themeParams: ThemeParams
  ready: () => void
  expand: () => void
  close: () => void
  HapticFeedback?: {
    impactOccurred: (s: 'light' | 'medium' | 'heavy') => void
    notificationOccurred: (t: 'error' | 'success' | 'warning') => void
  }
  MainButton: {
    text: string
    show: () => void
    hide: () => void
    enable: () => void
    disable: () => void
    showProgress: (leaveActive?: boolean) => void
    hideProgress: () => void
    setText: (t: string) => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  BackButton: {
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  showAlert: (message: string) => void
  showConfirm: (message: string, cb: (ok: boolean) => void) => void
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp }
  }
}

export const tg = window.Telegram?.WebApp

export const isTelegram = Boolean(tg?.initData)

export function initTelegram(): void {
  if (!tg) return
  tg.ready()
  tg.expand()
  applyTheme()
}

function applyTheme(): void {
  if (!tg) return
  const root = document.documentElement
  for (const [key, value] of Object.entries(tg.themeParams ?? {})) {
    root.style.setProperty(`--tg-${key.replace(/_/g, '-')}`, value)
  }
  root.dataset.scheme = tg.colorScheme
}

export function haptic(kind: 'light' | 'medium' | 'heavy' = 'light'): void {
  tg?.HapticFeedback?.impactOccurred(kind)
}

export function notify(message: string): void {
  if (tg) tg.showAlert(message)
  else window.alert(message)
}

export function confirmAction(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (tg) tg.showConfirm(message, resolve)
    else resolve(window.confirm(message))
  })
}

/** Игра, на которую пришли по ссылке. Два источника:
 *  - диплинк t.me/<bot>?startapp=g123 → start_param = "g123"
 *    (личное приглашение — "g123_i45": игра 123, позвал игрок 45)
 *  - кнопка бота с web_app url …/?game=123 → query-параметр */
const START = /^g(\d+)(?:_i(\d+))?$/

/** Кто позвал по личной ссылке: startapp=g<игра>_i<игрок>. */
export function initialInviterId(): number | null {
  const inviter = tg?.initDataUnsafe?.start_param?.match(START)?.[2]
  if (inviter) return Number(inviter)
  // Кнопка бота после /start g21_i5 открывает …/?game=21&inviter=5
  const fromQuery = new URLSearchParams(window.location.search).get('inviter')
  return fromQuery && /^\d+$/.test(fromQuery) ? Number(fromQuery) : null
}

/** Заявки на корт (v0.35): startapp=b<клуб> или ?book=<клуб> — экран брони;
 *  startapp=d<клуб> или ?desk=<клуб> — кабинет клуба; ?bookings=1 — мои брони. */
export function initialBookClub(): number | null {
  const m = tg?.initDataUnsafe?.start_param?.match(/^b(\d+)$/)
  if (m) return Number(m[1])
  const q = new URLSearchParams(window.location.search).get('book')
  return q && /^\d+$/.test(q) ? Number(q) : null
}

export function initialDesk(): number | null {
  const m = tg?.initDataUnsafe?.start_param?.match(/^d(\d+)$/)
  if (m) return Number(m[1])
  const q = new URLSearchParams(window.location.search).get('desk')
  return q && /^\d+$/.test(q) ? Number(q) : null
}

export function initialMyBookings(): boolean {
  return new URLSearchParams(window.location.search).has('bookings')
}

export function initialGameId(): number | null {
  const fromStart = tg?.initDataUnsafe?.start_param?.match(START)
  if (fromStart) return Number(fromStart[1])
  const fromQuery = new URLSearchParams(window.location.search).get('game')
  return fromQuery && /^\d+$/.test(fromQuery) ? Number(fromQuery) : null
}

/** Разрешение боту писать в личку. Без него игрок, пришедший по ссылке
 *  из группы и ни разу не нажавший /start, не получит ни одного напоминания —
 *  а напоминания и есть половина ценности продукта. */
export function askWriteAccess(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!tg?.requestWriteAccess) return resolve(false)
    try {
      tg.requestWriteAccess((granted) => resolve(granted))
    } catch {
      resolve(false)
    }
  })
}

/** Может ли бот писать игроку в личку. Флаг приходит в initData и относится
 *  к тому боту, через которого открыто приложение. После переезда на нового
 *  бота у всех он будет false, пока игрок не нажмёт Start или не разрешит. */
export function canBotWrite(): boolean {
  return tg?.initDataUnsafe?.user?.allows_write_to_pm !== false
}

const isNativeShell = Boolean(
  (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.(),
)

/** В нативной оболочке внешние ссылки (карты, WhatsApp, t.me) открывает
 *  система: window.open внутри WKWebView их не откроет. */
function openSystem(url: string): void {
  void import('./platform').then((p) => p.openOutside(url))
}

export function openExternal(url: string): void {
  if (tg) tg.openLink(url)
  else if (isNativeShell) openSystem(url)
  else window.open(url, '_blank', 'noopener')
}

/** Открыть файл по ссылке: в Telegram 8.0+ — скачивание средствами
 *  Telegram, в приложении — системный просмотрщик (там есть «Поделиться»),
 *  в браузере — новая вкладка. Вызывать прямо из обработчика нажатия. */
export function openFile(url: string, fileName: string): void {
  if (tg?.downloadFile && tg.isVersionAtLeast?.('8.0')) {
    tg.downloadFile({ url, file_name: fileName })
    return
  }
  openExternal(url)
}

/** Системный диалог «Поделиться»: пользователь сам выбирает чат,
 *  например вашу существующую группу. Никакой второй группы не нужно. */
/** Ссылка t.me внутри Telegram открывается чатом, а не браузером */
export function openTelegram(url: string): void {
  if (tg && url.startsWith('https://t.me/')) tg.openTelegramLink(url)
  else openExternal(url)
}

export function shareToChat(url: string, text: string): void {
  const link = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
  if (tg) tg.openTelegramLink(link)
  else if (isNativeShell) openSystem(link)
  else window.open(link, '_blank', 'noopener')
}

/** Можно ли отправить карточку игры (Telegram 8.0+). В старых клиентах
 *  и вне Telegram — нет, тогда делимся обычной ссылкой. */
export function canShareCard(): boolean {
  return Boolean(tg?.shareMessage && tg.isVersionAtLeast?.('8.0'))
}

/** Открывает выбор чата и отправляет туда подготовленную карточку.
 *  true — отправлено, false — пользователь закрыл диалог. */
export function shareCard(preparedId: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (!tg?.shareMessage) return reject(new Error('shareMessage unsupported'))
    try {
      tg.shareMessage(preparedId, (sent) => resolve(Boolean(sent)))
    } catch (e) {
      reject(e)
    }
  })
}

/** Кнопка «Назад». В Telegram — системная в шапке; вне Telegram своей шапки
 *  нет, поэтому обработчик кладётся сюда, а App рисует полоску «‹ Назад». */
type Back = () => void
let backHandler: Back | null = null
const backListeners = new Set<() => void>()

export function subscribeBack(listener: () => void): () => void {
  backListeners.add(listener)
  return () => backListeners.delete(listener)
}

export function currentBack(): Back | null {
  return backHandler
}

function setBack(handler: Back | null): void {
  backHandler = handler
  backListeners.forEach((l) => l())
}

/** Возвращает функцию снятия. */
export function useBackButton(onBack: () => void): () => void {
  if (!tg?.initData) {
    setBack(onBack)
    return () => {
      if (backHandler === onBack) setBack(null)
    }
  }
  tg.BackButton.onClick(onBack)
  tg.BackButton.show()
  return () => {
    tg.BackButton.offClick(onBack)
    tg.BackButton.hide()
  }
}

export function userLanguage(): string {
  return tg?.initDataUnsafe?.user?.language_code?.slice(0, 2) ?? 'ru'
}

/** Копирует текст в буфер. В webview Telegram clipboard API бывает недоступен —
 *  тогда запасной способ через скрытое поле. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const area = document.createElement('textarea')
    area.value = text
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    document.body.removeChild(area)
    return ok
  }
}
